import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { ActionCreator } from "actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { selectVersion, selectBuildno } from "store/selectors/application";

import "containers/Update/styles.scss";

const CHECK_INTERVAL_MS = 300000;

// mapStateToProps also selected `displayName`/`pages`, and mergeProps bound `logout` - none are
// read anywhere in this file, dropped rather than ported as dead state. `match.url` (via
// withRouterCompat's no-pattern fallback) was just `location.pathname` - read directly here.
// `state.updateInstalling` was also dead: nothing in the class ever set it, so the
// LoadingAnimation branch it gated was unreachable - dropped along with it.
export function Update() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("translation");
  const location = useLocation();
  const version = useAppSelector(selectVersion);
  const buildno = useAppSelector(selectBuildno);

  const isPlaying =
    location.pathname.indexOf("launch.real") > -1 ||
    location.pathname.indexOf("launch.demo") > -1;

  const [updateAvailable, setUpdateAvailable] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // checkForUpdate below is registered once via setInterval; these refs keep it reading the
  // latest version/buildno instead of the values captured on the render that scheduled it.
  const versionRef = useRef(version);
  const buildnoRef = useRef(buildno);

  useEffect(() => {
    versionRef.current = version;
    buildnoRef.current = buildno;
  }, [version, buildno]);

  useEffect(() => {
    function checkForUpdate() {
      dispatch(ActionCreator.checkForUpdate() as any).then((response: any) => {
        if (response.buildno !== buildnoRef.current) {
          dispatch(ActionCreator.broadcastNewUpdate(versionRef.current, buildnoRef.current) as any);
          setUpdateAvailable(true);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      });
    }

    intervalRef.current = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [dispatch]);

  if (isPlaying || !updateAvailable) {
    return null;
  }

  return (
    <div className="update-container modal" role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-body">
            <p>{t("newVersion")}</p>
            <p>{t("pleaseRefresh")}</p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-danger" onClick={() => window.location.reload()}>
              {t("refresh")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Update;
